import { motion, useReducedMotion } from 'framer-motion';
import { Brain, Activity, Sparkles, ShieldCheck } from 'lucide-react';

const FEATURES = [
  {
    icon: Brain,
    label: 'Personalized Insights',
    detail: 'AI tuned to your relationship patterns',
    glow: '#9B4FE8',
  },
  {
    icon: Activity,
    label: 'Emotional Rhythm',
    detail: 'Track tone & connection over time',
    glow: '#E8179B',
  },
  {
    icon: Sparkles,
    label: 'Trust Tracking',
    detail: 'Detect small shifts before they compound',
    glow: '#6B4FE0',
  },
  {
    icon: ShieldCheck,
    label: 'Private & Secure',
    detail: 'Your data stays yours, always',
    glow: '#00A8F0',
  },
] as const;

export function HeroFeatureStrip() {
  const rm = useReducedMotion();

  return (
    <section
      id="features"
      className="relative z-10 mx-auto w-full px-5"
      style={{ maxWidth: 'min(1080px, 94vw)', marginTop: 4, marginBottom: 0 }}
    >
      {/* Ambient floor glow beneath the dock */}
      <div
        className="pointer-events-none absolute inset-x-10 -bottom-10 h-12 opacity-40"
        style={{
          background: 'radial-gradient(ellipse 70% 100% at 50% 0%, rgba(155,79,232,.22), transparent 100%)',
          filter: 'blur(16px)',
        }}
      />

      <motion.div
        className="relative overflow-hidden"
        style={{
          backgroundImage: 'url(/assets/tension/hero/hero-feature-strip-glass.png)',
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          borderRadius: 22,
          /* Luxury black glass */
          backgroundColor: 'rgba(4, 8, 20, 0.80)',
          backdropFilter: 'blur(36px) saturate(1.5)',
          WebkitBackdropFilter: 'blur(36px) saturate(1.5)',
          boxShadow: [
            /* top inner specular — glass thickness */
            '0 1px 0 rgba(255,255,255,.10) inset',
            /* bottom inner shadow — depth */
            '0 -1px 0 rgba(0,0,0,.20) inset',
            /* outer lift */
            '0 2px 6px rgba(0,0,0,.30)',
            '0 18px 52px rgba(0,0,0,.56)',
            /* outer ring */
            '0 0 0 1px rgba(255,255,255,.05)',
          ].join(', '),
        }}
        initial={{ opacity: 0, y: rm ? 0 : 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.76, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Top specular — very subtle, physical glass realism */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[1px]"
          style={{
            background: 'linear-gradient(90deg, transparent 4%, rgba(255,255,255,.09) 28%, rgba(255,255,255,.12) 50%, rgba(255,255,255,.09) 72%, transparent 96%)',
          }}
        />

        {/* Inner depth gradient — creates thickness illusion */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,.03) 0%, transparent 40%, rgba(0,0,0,.08) 100%)',
          }}
        />

        <div className="relative grid grid-cols-2 sm:grid-cols-4">
          {FEATURES.map((feat, i) => {
            const Icon = feat.icon;
            const isNotLast = i < FEATURES.length - 1;
            return (
              <motion.div
                key={feat.label}
                className="group relative flex flex-col items-center gap-4 px-6 py-9 text-center transition-colors duration-400 hover:bg-white/[0.022]"
                initial={{ opacity: 0, y: rm ? 0 : 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 + 0.08, duration: 0.64, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Atmospheric vertical separator — gradient, not hard border */}
                {isNotLast && (
                  <div
                    className="pointer-events-none absolute right-0 top-[18%] bottom-[18%] w-px hidden sm:block"
                    style={{
                      background: 'linear-gradient(180deg, transparent, rgba(255,255,255,.055) 30%, rgba(255,255,255,.055) 70%, transparent)',
                    }}
                  />
                )}

                {/* Horizontal separator for 2-col mobile — bottom two rows */}
                {i < 2 && (
                  <div
                    className="pointer-events-none absolute inset-x-[12%] bottom-0 h-px sm:hidden"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.055) 40%, rgba(255,255,255,.055) 60%, transparent)',
                    }}
                  />
                )}

                {/* Icon — well centered, with environmental glow */}
                <motion.div
                  className="flex h-11 w-11 items-center justify-center rounded-[14px]"
                  style={{
                    background: `linear-gradient(135deg, ${feat.glow}1A, ${feat.glow}08)`,
                    border: `1px solid ${feat.glow}25`,
                    boxShadow: `0 0 18px ${feat.glow}0E`,
                  }}
                  whileHover={rm ? undefined : {
                    scale: 1.10,
                    transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  }}
                >
                  <Icon className="h-5 w-5 stroke-[1.6]" style={{ color: feat.glow }} />
                </motion.div>

                {/* Typography — more breathing room */}
                <div className="space-y-1.5">
                  <p className="text-[13.5px] font-semibold leading-tight"
                     style={{ color: 'rgba(244,246,252,.92)' }}>
                    {feat.label}
                  </p>
                  <p className="text-[11.5px] leading-relaxed"
                     style={{ color: 'rgba(244,246,252,.42)' }}>
                    {feat.detail}
                  </p>
                </div>

                {/* Hover accent — bottom glow line */}
                <motion.div
                  className="absolute inset-x-8 bottom-0 h-px rounded-full"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${feat.glow}BB, transparent)`,
                    boxShadow: `0 0 8px ${feat.glow}60`,
                  }}
                  initial={{ opacity: 0, scaleX: 0 }}
                  whileHover={rm ? undefined : { opacity: 1, scaleX: 1 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Bottom specular — very subtle */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px]"
          style={{ background: 'linear-gradient(90deg, transparent 8%, rgba(255,255,255,.035) 50%, transparent 92%)' }}
        />
      </motion.div>
    </section>
  );
}
